/**
 * Valnexo CRM - Academy Page
 * ===========================
 * Page principale de la section formation avec:
 * - Liste des modules par catégorie
 * - Progression globale
 * - Accès aux modules
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { academyAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import {
    GraduationCap, BookOpen, Play, CheckCircle2, Clock, Award,
    Zap, HardHat, Monitor, BarChart3, Users, ChevronRight,
    Plus, Settings, Lock, ShieldCheck, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

// Icônes par catégorie
const CATEGORY_ICONS = {
    crm_valnexo: Monitor,
    easy_power: Zap,
    chantier: HardHat,
    general: BookOpen
};

const CATEGORY_COLORS = {
    crm_valnexo: 'bg-blue-100 text-blue-700 border-blue-200',
    easy_power: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    chantier: 'bg-orange-100 text-orange-700 border-orange-200',
    general: 'bg-slate-100 text-slate-700 border-slate-200'
};

const CATEGORY_NAMES = {
    crm_valnexo: 'CRM Valnexo',
    easy_power: 'Courtage',
    chantier: 'Travaux d’optimisation',
    general: 'Général'
};

// Carte de module
const ModuleCard = ({ module, onClick }) => {
    const CategoryIcon = CATEGORY_ICONS[module.category] || BookOpen;
    const progress = module.user_progress || {};
    const isCompleted = progress.completed;
    const hasStarted = progress.progress_percent > 0;
    
    return (
        <Card 
            className={cn(
                "cursor-pointer transition-all hover:shadow-lg hover:border-primary/50",
                isCompleted && "border-emerald-300 bg-emerald-50/30"
            )}
            onClick={onClick}
            data-testid={`module-card-${module.id}`}
        >
            <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                    <Badge 
                        variant="outline" 
                        className={cn("text-xs", CATEGORY_COLORS[module.category])}
                    >
                        <CategoryIcon className="h-3 w-3 mr-1" />
                        {CATEGORY_NAMES[module.category]}
                    </Badge>
                    <div className="flex items-center gap-1.5">
                        {progress.content_has_updates && (
                            <Badge 
                                variant="outline" 
                                className="text-[10px] py-0 h-5 border-indigo-300 text-indigo-700 bg-indigo-50 animate-pulse"
                                data-testid="badge-content-update"
                            >
                                <Play className="h-3 w-3 mr-0.5" />
                                Nouveau contenu
                            </Badge>
                        )}
                        {progress.quiz_has_updates && (
                            <Badge 
                                variant="outline" 
                                className="text-[10px] py-0 h-5 border-orange-300 text-orange-700 bg-orange-50 animate-pulse"
                                data-testid="badge-quiz-update"
                            >
                                <Sparkles className="h-3 w-3 mr-0.5" />
                                Mise à jour
                            </Badge>
                        )}
                        {isCompleted && (
                            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                        )}
                        {!module.is_published && (
                            <Badge variant="secondary" className="text-xs">
                                <Lock className="h-3 w-3 mr-1" />
                                Brouillon
                            </Badge>
                        )}
                    </div>
                </div>
                <CardTitle className="text-lg mt-2">{module.title}</CardTitle>
                <CardDescription className="line-clamp-2">
                    {module.description}
                </CardDescription>
            </CardHeader>
            <CardContent className="pb-3">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {module.estimated_duration_minutes} min
                    </div>
                    <div className="flex items-center gap-1">
                        <BookOpen className="h-4 w-4" />
                        {module.contents_count || 0} contenus
                    </div>
                    {module.has_quiz && (
                        <div className="flex items-center gap-1">
                            <Award className="h-4 w-4" />
                            Quiz
                        </div>
                    )}
                </div>
            </CardContent>
            <CardFooter className="pt-0">
                <div className="w-full space-y-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Progression</span>
                        <span className="font-medium">{progress.progress_percent || 0}%</span>
                    </div>
                    <Progress value={progress.progress_percent || 0} className="h-2" />
                    {progress.quiz_passed && (
                        <div className="flex items-center gap-1 text-xs text-emerald-600">
                            <CheckCircle2 className="h-3 w-3" />
                            Quiz réussi ({progress.quiz_score}%)
                        </div>
                    )}
                </div>
            </CardFooter>
        </Card>
    );
};

// Stats globales utilisateur
const UserStats = ({ stats }) => {
    if (!stats) return null;
    
    return (
        <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mb-6">
            <Card>
                <CardContent className="pt-4 px-3 sm:px-6">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                            <BookOpen className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-lg sm:text-2xl font-bold truncate">{stats.completed_modules}/{stats.total_modules}</p>
                            <p className="text-[11px] sm:text-xs text-muted-foreground">Modules complétés</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardContent className="pt-4 px-3 sm:px-6">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg shrink-0">
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-lg sm:text-2xl font-bold">{stats.completion_rate}%</p>
                            <p className="text-[11px] sm:text-xs text-muted-foreground">Taux de complétion</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardContent className="pt-4 px-3 sm:px-6">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg shrink-0">
                            <Clock className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-lg sm:text-2xl font-bold truncate">{stats.total_time_spent_formatted}</p>
                            <p className="text-[11px] sm:text-xs text-muted-foreground">Temps de formation</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardContent className="pt-4 px-3 sm:px-6">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="p-2 bg-yellow-100 rounded-lg shrink-0">
                            <Award className="h-5 w-5 text-yellow-600" />
                        </div>
                        <div className="min-w-0">
                            <Progress value={stats.completion_rate} className="h-2 w-full sm:w-20" />
                            <p className="text-[11px] sm:text-xs text-muted-foreground mt-1">Progression globale</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default function Academy() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [modules, setModules] = useState([]);
    const [myProgress, setMyProgress] = useState(null);
    const [activeCategory, setActiveCategory] = useState('all');
    
    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const isVP = user?.role === 'vp';

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [modulesRes, progressRes] = await Promise.all([
                academyAPI.getModules(),
                academyAPI.getMyProgress()
            ]);
            setModules(modulesRes.data || []);
            setMyProgress(progressRes.data);
        } catch (err) {
            console.error('Erreur chargement academy:', err);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const filteredModules = activeCategory === 'all' 
        ? modules 
        : modules.filter(m => m.category === activeCategory);

    const modulesWithNewContent = modules.filter(m => m.user_progress?.content_has_updates);

    // Grouper par catégorie pour l'affichage
    const modulesByCategory = modules.reduce((acc, module) => {
        const cat = module.category || 'general';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(module);
        return acc;
    }, {});

    // N'afficher que les onglets de catégorie qui contiennent au moins un module
    // visible par l'utilisateur — évite les catégories vides trompeuses (ex : parrain
    // qui voit "Courtage" alors qu'il n'a aucun module Courtage).
    const availableCategories = new Set(Object.keys(modulesByCategory));

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-10 w-64" />
                    <div className="grid gap-4 md:grid-cols-4">
                        {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
                    </div>
                    <div className="grid gap-4 md:grid-cols-3">
                        {[1,2,3].map(i => <Skeleton key={i} className="h-48" />)}
                    </div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="academy-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                        <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                            <GraduationCap className="h-6 w-6 sm:h-7 sm:w-7 text-primary shrink-0" />
                            Académie
                        </h1>
                        <p className="text-muted-foreground text-sm">
                            Formez-vous aux outils et processus Valnexo
                        </p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {isAdmin && (
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => navigate('/academy/admin')}
                                data-testid="admin-dashboard-btn"
                                className="text-xs sm:text-sm"
                            >
                                <BarChart3 className="h-4 w-4 mr-1.5 sm:mr-2" />
                                Dashboard Admin
                            </Button>
                        )}
                        {isVP && (
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => navigate('/academy/supervision')}
                                data-testid="supervision-dashboard-btn"
                                className="text-xs sm:text-sm"
                            >
                                <ShieldCheck className="h-4 w-4 mr-1.5 sm:mr-2" />
                                Dashboard Supervision
                            </Button>
                        )}
                        {isAdminFull && (
                            <Button 
                                size="sm"
                                onClick={() => navigate('/academy/manage')}
                                data-testid="manage-modules-btn"
                                className="text-xs sm:text-sm"
                            >
                                <Plus className="h-4 w-4 mr-1.5 sm:mr-2" />
                                Gérer les modules
                            </Button>
                        )}
                    </div>
                </div>

                {/* Bannière « nouvelle vidéo » — indique clairement où se mettre à jour */}
                {modulesWithNewContent.length > 0 && (
                    <div
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3"
                        data-testid="academy-new-content-banner"
                    >
                        <div className="flex items-start gap-3">
                            <div className="rounded-lg bg-indigo-600 p-2 text-white shrink-0">
                                <Play className="h-5 w-5" />
                            </div>
                            <div>
                                <p className="font-semibold text-indigo-900">
                                    {modulesWithNewContent.length === 1
                                        ? 'Une nouvelle vidéo est disponible'
                                        : `${modulesWithNewContent.length} modules ont du nouveau contenu`}
                                </p>
                                <p className="text-sm text-indigo-700">
                                    À mettre à jour : {modulesWithNewContent.map(m => m.title).join(', ')}
                                </p>
                            </div>
                        </div>
                        <Button
                            className="bg-indigo-600 hover:bg-indigo-700 shrink-0"
                            onClick={() => navigate(`/academy/module/${modulesWithNewContent[0].id}`)}
                            data-testid="academy-new-content-cta"
                        >
                            <Play className="h-4 w-4 mr-2" />
                            Me mettre à jour
                        </Button>
                    </div>
                )}

                {/* Stats utilisateur */}
                <UserStats stats={myProgress?.stats} />

                {/* Filtres par catégorie */}
                <Tabs value={activeCategory} onValueChange={setActiveCategory}>
                    <TabsList>
                        <TabsTrigger value="all">
                            Tous les modules
                        </TabsTrigger>
                        {availableCategories.has('crm_valnexo') && (
                            <TabsTrigger value="crm_valnexo">
                                <Monitor className="h-4 w-4 mr-1" />
                                CRM Valnexo
                            </TabsTrigger>
                        )}
                        {availableCategories.has('easy_power') && (
                            <TabsTrigger value="easy_power">
                                <Zap className="h-4 w-4 mr-1" />
                                Courtage
                            </TabsTrigger>
                        )}
                        {availableCategories.has('chantier') && (
                            <TabsTrigger value="chantier">
                                <HardHat className="h-4 w-4 mr-1" />
                                Travaux d’optimisation
                            </TabsTrigger>
                        )}
                        {availableCategories.has('general') && (
                            <TabsTrigger value="general">
                                <BookOpen className="h-4 w-4 mr-1" />
                                Général
                            </TabsTrigger>
                        )}
                    </TabsList>

                    <TabsContent value={activeCategory} className="mt-6">
                        {filteredModules.length === 0 ? (
                            <Card>
                                <CardContent className="py-12 text-center">
                                    <GraduationCap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground">
                                        Aucun module disponible dans cette catégorie
                                    </p>
                                    {isAdminFull && (
                                        <Button 
                                            className="mt-4" 
                                            onClick={() => navigate('/academy/manage')}
                                        >
                                            <Plus className="h-4 w-4 mr-2" />
                                            Créer un module
                                        </Button>
                                    )}
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {filteredModules.map(module => (
                                    <ModuleCard
                                        key={module.id}
                                        module={module}
                                        onClick={() => navigate(`/academy/module/${module.id}`)}
                                    />
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
}
